import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { timeRangeFilterId, virtualMachineFilterId } from '../../filters';

const title = t`Virtual Machine Disk Latency (Max)`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'server',
        metrics: ['vmDiskMaxLatency', 'statusMaxDiskLatency'],
    },
};

export function useChartConfig() {
    const color = useMetricColor('serverCpu');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Latency (ms)`,
                    },
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'statusMaxDiskLatency',
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.DECIMAL,
                    units: ' ms',
                    chartType: 'spline',
                    key: 'vmDiskMaxLatency',
                    label: t`Max Latency`,
                    color,
                },
            ],
        }),
        [color]
    );
}

function VirtualMachineDiskLatencyPanel() {
    const chartConfig = useChartConfig();
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

VirtualMachineDiskLatencyPanel.propTypes = panelPropTypes;

VirtualMachineDiskLatencyPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareVmDiskLatencyTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.VIRTUAL_MACHINE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Maximum disk latency trend for a selected virtual machine (VM)`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, virtualMachineFilterId],
        optional: [],
    },
})(VirtualMachineDiskLatencyPanel);
